/**
 * useKeyboardNavigation Hook
 * 
 * Handles keyboard navigation for dropdown menus and accordions.
 * Provides arrow key navigation, Enter to select, and Escape to close.
 * 
 * @param isOpen - Whether the menu/dropdown is open
 * @param itemCount - Number of items in the menu
 * @param onNavigate - Callback when item is selected via keyboard
 * @param onClose - Callback to close the menu
 * @param focusedIndex - Current focused item index
 * @param setFocusedIndex - Function to update focused index
 * 
 * @example
 * const [focusedIndex, setFocusedIndex] = useState(0);
 * const handleKeyDown = useKeyboardNavigation({
 *   isOpen,
 *   itemCount: items.length,
 *   onNavigate: (index) => navigate(items[index].to),
 *   onClose: () => setIsOpen(false),
 *   focusedIndex,
 *   setFocusedIndex
 * });
 */

import { useCallback } from "react";

export interface UseKeyboardNavigationOptions {
    isOpen: boolean;
    itemCount: number;
    onNavigate: (index: number) => void;
    onClose: () => void;
    focusedIndex: number;
    setFocusedIndex: (value: number | ((prev: number) => number)) => void;
}

export function useKeyboardNavigation({
    isOpen,
    itemCount,
    onNavigate,
    onClose,
    focusedIndex,
    setFocusedIndex,
}: UseKeyboardNavigationOptions) {
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!isOpen && event.key === "Enter") {
                event.preventDefault();
                return;
            }

            if (!isOpen) return;

            switch (event.key) {
                case "Escape":
                    event.preventDefault();
                    onClose();
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    // Use functional setState to avoid stale closure
                    setFocusedIndex((prev) => (prev + 1) % itemCount);
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    // Use functional setState to avoid stale closure
                    setFocusedIndex((prev) => (prev - 1 + itemCount) % itemCount);
                    break;
                case "Enter":
                    event.preventDefault();
                    onNavigate(focusedIndex);
                    break;
            }
        },
        [isOpen, itemCount, onNavigate, onClose, focusedIndex, setFocusedIndex]
    );

    return handleKeyDown;
}
